import {TEAM_ROLES, getTeamRole, type TeamRoleId} from '../src/team';
import {PRODUCT_ENTRY_LABEL} from '../src/product';
import {DEMOS, type PublicDemo} from './demo-catalog';
import {RoleIcon} from './role-icon';
import {RoleInstructions} from './role-instructions';
import {APP_BASE, Link} from './router';
import {resolvePublicPath, teamRolePath, teamWorkPath} from './public-paths';
import './agent-pages.css';

/** Published files stay within the selected role's public work directory. */
export function publicWorkFileUrl(roleId: TeamRoleId, file: string): string {
  const segments = file.split('/');
  if (!segments.length || segments.some(segment => !segment || segment === '.' || segment === '..' || /[\\?#\u0000-\u001f]/.test(segment))) {
    throw new Error(`Invalid public work file for ${roleId}: ${file}`);
  }
  return `${APP_BASE}/work/${roleId}/${segments.map(segment => encodeURIComponent(segment)).join('/')}`;
}

function PublishedDemoNote() {
  return <p className="agent-demo-note">Public work demo</p>;
}

function TeamNavigation({roleId}: {roleId: TeamRoleId}) {
  return <nav className="agent-page-team" aria-label="Explore the team"><h2>Explore the team</h2>
    <div>{TEAM_ROLES.map(member => <Link key={member.id} href={`${APP_BASE}${teamRolePath(member.id)}`} aria-current={member.id === roleId ? 'page' : undefined}>
      <RoleIcon roleId={member.id} size={32}/><span><span className="agent-team-discipline">{member.discipline}</span><strong>{member.name}</strong></span>
    </Link>)}</div>
  </nav>;
}

function RoleIntroduction({roleId}: {roleId: TeamRoleId}) {
  const role = getTeamRole(roleId);
  return <header className="agent-page-intro"><div className="agent-role-heading"><RoleIcon roleId={roleId} size={64}/>
    <div><p className="agent-page-eyebrow">{role.discipline}</p><h1>{role.name}</h1></div></div>
    {role.description && <p className="agent-page-description">{role.description}</p>}
    <Link className="agent-page-text-link" href={`${APP_BASE}/workspace?role=${role.id}`}>{PRODUCT_ENTRY_LABEL}</Link>
  </header>;
}

function DemoDetails({demo}: {demo: PublicDemo}) {
  return <div className="agent-demo-details">
    <section><h3>How the work was done</h3><ul>{demo.process.map(item => <li key={item}>{item}</li>)}</ul></section>
    <section><h3>What was delivered</h3><ul>{demo.delivered.map(item => <li key={item}>{item}</li>)}</ul></section>
  </div>;
}

function DemoUnavailable() {
  return <section className="agent-demo-unavailable"><h2>The public demo is not available yet.</h2></section>;
}

export function agentRoleFromPath(path: string): TeamRoleId | null {
  const resolvedPath = resolvePublicPath(path);
  return TEAM_ROLES.find(role => resolvedPath === teamRolePath(role.id))?.id ?? null;
}

export function agentWorkFromPath(path: string): TeamRoleId | null {
  const resolvedPath = resolvePublicPath(path);
  return TEAM_ROLES.find(role => resolvedPath === teamWorkPath(role.id))?.id ?? null;
}

export function AgentPage({roleId}: {roleId: TeamRoleId}) {
  const demo = DEMOS[roleId];
  return <article className="agent-page"><RoleIntroduction roleId={roleId}/>
    <RoleInstructions roleId={roleId}/>
    {demo ? <><section className="agent-demo-summary" aria-labelledby="agent-demo-title">
      <div className="agent-demo-request"><p className="agent-page-eyebrow">The assignment</p><blockquote>{demo.assignment}</blockquote></div>
      <div className="agent-demo-result"><PublishedDemoNote/><h2 id="agent-demo-title">{demo.title}</h2><p className="agent-demo-excerpt">{demo.excerpt}</p>
        <Link className="agent-page-action" href={`${APP_BASE}${teamWorkPath(roleId)}`}>Open the work</Link>
      </div></section><DemoDetails demo={demo}/></> : <DemoUnavailable/>}
    <TeamNavigation roleId={roleId}/>
  </article>;
}

const artifactKindLabels: Record<PublicDemo['artifacts'][number]['kind'], string> = {
  html: 'Web page', pdf: 'PDF', markdown: 'Document', text: 'Text', code: 'Source code', data: 'Data', archive: 'Archive'
};

function ArtifactList({roleId, demo}: {roleId: TeamRoleId; demo: PublicDemo}) {
  return <section className="agent-work-files" aria-labelledby="agent-work-files-title"><h2 id="agent-work-files-title">Files from this work</h2>
    <ul>{demo.artifacts.map(artifact => {
      const url = publicWorkFileUrl(roleId, artifact.file);
      return <li key={artifact.file}><div className="agent-artifact-description"><p className="agent-page-eyebrow">{artifactKindLabels[artifact.kind]}</p>
        <h3><a href={url} target="_blank" rel="noopener noreferrer">{artifact.name}</a></h3><p>{artifact.description}</p></div>
        <div className="agent-artifact-actions"><a href={url} target="_blank" rel="noopener noreferrer" aria-label={`Open ${artifact.name} in a new tab`}>Open</a>
          <a href={url} download aria-label={`Download ${artifact.name}`}>Download</a></div>
      </li>;
    })}</ul>
  </section>;
}

export function AgentWorkPage({roleId}: {roleId: TeamRoleId}) {
  const role = getTeamRole(roleId);
  const demo = DEMOS[roleId];
  const preview = demo?.previewFile ? demo.artifacts.find(artifact => artifact.file === demo.previewFile) : undefined;
  if (demo?.previewFile && (!preview || preview.kind !== 'html')) {
    throw new Error(`The public preview for ${roleId} must refer to an HTML file in its artifact list.`);
  }
  return <article className="agent-page agent-work-page"><Link className="agent-work-back" href={`${APP_BASE}${teamRolePath(roleId)}`}>About {role.name}</Link>
    {demo ? <>
      <header className="agent-work-intro"><div className="agent-work-author"><RoleIcon roleId={roleId} size={64}/><div><PublishedDemoNote/><p>{role.name}</p></div></div>
        <h1>{demo.title}</h1><p className="agent-demo-excerpt">{demo.excerpt}</p>
        {preview && <a className="agent-page-action" href={publicWorkFileUrl(roleId, preview.file)} target="_blank" rel="noopener noreferrer">Open full preview</a>}
      </header>
      {preview && <section className="agent-work-preview" aria-label={`${demo.title} preview`}><div className="agent-work-preview-heading"><h2>Work preview</h2><p>{preview.description}</p></div>
        <iframe key={publicWorkFileUrl(roleId, preview.file)} src={publicWorkFileUrl(roleId, preview.file)} title={`${demo.title} — finished work`} sandbox="allow-scripts allow-forms allow-downloads allow-modals" referrerPolicy="no-referrer" loading="lazy"/>
      </section>}
      <ArtifactList roleId={roleId} demo={demo}/>
      <section className="agent-work-assignment"><p className="agent-page-eyebrow">The assignment</p><blockquote>{demo.assignment}</blockquote></section>
      <DemoDetails demo={demo}/>
      <section className="agent-work-next"><h2>Give {role.name} your next task.</h2><Link className="agent-page-action" href={`${APP_BASE}/workspace?role=${roleId}`}>{PRODUCT_ENTRY_LABEL}</Link></section>
    </> : <DemoUnavailable/>}
    <TeamNavigation roleId={roleId}/>
  </article>;
}
